<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { useQuery } from '@tanstack/vue-query'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { getUserDetailAction } from '@/api/functions/users/get-user-detail.action'
import { getUserInfoAction } from '@/api/functions/users/get-user-info.action'
import { getUserLrfmAction } from '@/api/functions/users/get-user-lrfm.action'
import { impersonateUserAction } from '@/api/functions/users/impersonate-user-action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { hasPermission } from '@/composables/useAuth'
import { useGeneralSettings } from '@/composables/useGeneralSettings'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import CreateRequestDrawer from '@/views/requisitions/partials/create-request/CreateRequestDrawer.vue'
import AdditionalInfoForm from './forms/AdditionalInfoForm.vue'
import EmailForm from './forms/EmailForm.vue'
import IdentityDocumentForm from './forms/IdentityDocumentForm.vue'
import MobileForm from './forms/MobileForm.vue'
import NationalCodeForm from './forms/NationalCodeForm.vue'
import RegistrationStatusForm from './forms/RegistrationStatusForm.vue'
import SelfieForm from './forms/SelfieForm.vue'

const toast = useToast()
const route = useRoute()
const userId = route.params.id as string
const copied = ref(false)
const isActive = ref(false)

const tabs = [
  { title: 'اطلاعات اولیه', link: `/users/${userId}` },
  { title: 'تخفیف‌ها', link: `/users/${userId}/discounts` },
  { title: 'شماره حساب‌ها', link: `/users/${userId}/accounts` },
  { title: 'تراکنش‌ها', link: `/users/${userId}/transactions` },
  { title: 'کاربران دعوت شده', link: `/users/${userId}/invited-users` },
]

// const showDeleteModal = ref(false);

const { userStatusOptions, discountOptions, currencies, countries, transferMethods } = useGeneralSettings()

const {
  data: userDetail,
  // isLoading: isUserDetailLoading,
  // isError: isUserDetailError,
  refetch: refetchDetail,
} = useQueryApi({
  queryKey: ['user-detail', userId],
  queryFn: () => getUserDetailAction(userId),
  enabled: !!userId,
})

const {
  data: userInfo,
  // isLoading: isUserInfoLoading,
  // isError: isUserInfoError,
  refetch: refetchInfo,
} = useQueryApi({
  queryKey: ['user-info', userId],
  queryFn: () => getUserInfoAction(userId),
  enabled: !!userId,
})

const {
  data: userLrfm,
  // isLoading: isUserLrfmLoading,
  // isError: isUserLrfmError,
  refetch: refetchLrfm,
} = useQueryApi({
  queryKey: ['user-lrfm', userId],
  queryFn: () => getUserLrfmAction(userId),
  enabled: !!userId,
})

const userData = computed(() => ({
  detail: userDetail.value?.data,
  info: userInfo.value?.data,
  lrfm: userLrfm.value?.data,
}))

const userStatusText = computed(() => {
  const status = userData?.value?.detail?.status

  switch (status) {
    case 'VERIFIED':
      return 'تایید شده'
    case 'EMAIL_VERIFICATION_PENDING':
      return 'ثبت نام اولیه'
    case 'MOBILE_REGISTRATION_PENDING':
      return 'در انتظار ثبت شماره موبایل'
    case 'IDENTIFICATION_REGISTRATION_PENDING':
      return 'در انتظار ارسال مدارک هویتی'
    case 'IDENTIFICATION_VERIFICATION_PENDING':
      return 'در انتظار تایید مدارک هویتی'
    case 'MOBILE_VERIFICATION_PENDING':
      return 'در انتظار تایید شماره موبایل'
    default:
      return ''
  }
})

const lrfmDays = computed(() => {
  const lrfm = userLrfm.value?.data
  if (!lrfm?.lifetime || !lrfm?.recency) return { L: '0', R: '0' }

  const _MS_PER_DAY = 1000 * 60 * 60 * 24
  const now = new Date().getTime()
  const lifetime = new Date(lrfm.lifetime).getTime()
  const recency = new Date(lrfm.recency).getTime()

  const L = Math.floor((now - lifetime) / _MS_PER_DAY)
  const R = Math.floor((now - recency) / _MS_PER_DAY)

  return { L, R }
})

//  Function to copy referral code to clipboard
const copyCode = async () => {
  try {
    await navigator.clipboard.writeText(userData?.value?.detail?.shortReferralCode || '')
    copied.value = true
    isActive.value = true
  } catch (err) {
    toast.error(getErrorMessage(err), 'خطا')
  }
}

//  Handle clicks outside copy button to reset state
const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (!target.closest('#copy-btn')) {
    copied.value = false
    isActive.value = false
  }
}

const refetchUserData = () => {
  refetchDetail()
  refetchInfo()
  refetchLrfm()
}

const canImpersonate = computed(() => hasPermission('impersonate-users'))

const impersonateMutation = useMutationApi({
  mutationFn: (id: string) => impersonateUserAction(id),
  onSuccess: (response) => {
    const redirectUrl = response?.data?.redirect_url
    if (redirectUrl) {
      window.open(redirectUrl, '_blank')
    } else {
      toast.error('لینک ورود دریافت نشد', 'خطا')
    }
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const isImpersonating = computed(() => impersonateMutation.isPending?.value ?? false)

const handleImpersonateUser = () => {
  if (!userId) return
  impersonateMutation.mutate(userId)
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="mb-9">

    <router-link to="/users"
      class="w-fit mb-4 gap-1 flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
      <img src="/svg/back-icon.svg" class="me-1" width="22" />
      <span>بازگشت</span>
    </router-link>

    <div class=" flex flex-col lg:flex-row lg:items-center justify-between mb-6">
      <div class="w-full lg:w-auto flex flex-row justify-between items-center gap-8 ">
        <div class="text-[#1b325a] text-[22px]">
          کاربر شماره <span class="font-fa">{{ userData?.info?.code }}</span>
        </div>
        <div class="w-auto flex flex-row items-center justify-between lg:justify-start gap-2">
          <!-- status  -->
          <div
            class="w-fit flex items-center bg-[#d5ddea] text-[#2d5396] justify-center text-[13px] font-medium h-10 rounded-[10px] px-5 me-1">
            {{ userStatusText }}
          </div>

          <!-- login as user -->
          <Button v-if="canImpersonate" @click="handleImpersonateUser" :disabled="isImpersonating"
            class="bg-transparent border-[#2d5396] border-2 hover:bg-gray-100 rounded-[10px]! px-4 h-10! py-0 text-[13px] font-normal">
            <span class="flex items-center justify-center gap-2 text-[#2d5396]">
              {{ isImpersonating ? 'در حال ورود...' : 'ورود به حساب کاربر' }}
            </span>
          </Button>
        </div>
      </div>
    </div>

    <!-- Tabs  -->
    <Tabs :tabs="tabs" :isAdminPanel="true" class="mt-8">
      <template #actions>
        <div class="flex items-center gap-2 pb-1">

          <CreateRequestDrawer :countries="countries" :currencies="currencies" :discount-options="discountOptions"
            :transfer-methods="transferMethods" />
        </div>
      </template>
    </Tabs>

    <div class="w-full flex flex-col  p-5 lg:p-8 bg-white  rounded-xl mt-8">
      <div class="flex flex-col lg:flex-row justify-between gap-6 lg:gap-[168.73px]">
        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">تاریخ ثبت نام اولیه:</span>
            <span dir="ltr" class="font-fa text-[#1b325a]">{{ userData?.detail?.createdAt ?
              stringToFaDateTime(userData?.detail?.createdAt) : "-" }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]"> نام و نام خانوادگی:</span>
            <span class="text-[#1b325a]">{{ userData?.detail?.fullName }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]"> کانال ورودی:</span>
            <span class=" text-[#1b325a]">{{ userData?.detail?.referralType === 'DIRECT' ? 'مستقیم' :
              'ریفرال' }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">کشور:</span>

            <div v-if="userData?.detail?.country" class="flex items-center gap-1  text-[#1b325a]">
              <img :src="userData?.detail?.country?.flagUrl" width="24" height="16" alt="flag" />
              {{ userData?.detail?.country?.title?.fa }}
            </div>
          </div>
        </div>
        <div class="w-full lg:w-1/2 flex flex-col gap-3">
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">تاریخ آخرین ویرایش:</span>
            <span dir="ltr" class="font-fa text-[#1b325a]">{{ userData?.detail?.updatedAt ?
              stringToFaDateTime(userData?.detail?.updatedAt) : "-" }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">ایمیل:</span>
            <span class="font-fa text-[#1b325a]">{{ userData?.detail?.email }}</span>
          </div>
          <div v-if="userData?.detail?.inviter?.name || userData?.detail?.inviter?.id"
            class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">معرفی شده توسط:</span>
            <span class=" text-[#1b325a]">
              {{ userData?.detail?.inviter?.name }}({{ userData?.detail?.inviter?.id }})
            </span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]">کد معرفی کاربر:</span>
            <span class=" text-[#1b325a]">{{ userData?.detail?.shortReferralCode }}</span>
          </div>
          <div class="w-full flex items-center justify-between  text-sm font-medium">
            <span class="text-[#2d5396]"></span>
            <div class="relative inline-block">
              <img id="copy-btn" @click.stop="copyCode" width="24" src="/svg/ic-invite.e61911c.svg" alt="copy-icon"
                class="cursor-pointer" />
              <transition name="pop">
                <div v-if="copied"
                  class="absolute w-[70px] top-0 -mt-[41px] px-4 py-1.5 text-sm italic text-nowrap font-bold text-start bg-[#dae4f3d9] text-[#2d5396] rounded-lg">
                  کپی شد
                </div>
              </transition>
            </div>

          </div>
        </div>
      </div>
    </div>

    <div class=" bg-white text-black rounded-xl divide-y lg:divide-x divide-[#ddd] grid lg:grid-cols-4 my-6 ">
      <div class="w-full flex items-center justify-between  text-sm font-medium p-4">
        <span class="text-[#2d5396]">طول عمر (L):</span>
        <span>{{ lrfmDays.L }}</span>
      </div>
      <div class="w-full flex items-center justify-between  text-sm font-medium p-4">
        <span class="text-[#2d5396]">تازگی تراکنش (R):</span>
        <span>{{ lrfmDays.R }}</span>
      </div>
      <div class="w-full flex items-center justify-between  text-sm font-medium p-4">
        <span class="text-[#2d5396]">تکرار تراکنش (F):</span>
        <span>{{ userData?.lrfm?.frequency ?? 0 }}</span>
      </div>
      <div class="w-full flex items-center justify-between  text-sm font-medium p-4">
        <span class="text-[#2d5396]">ارزش ارزی تراکنش‌ها (M):</span>
        <span>€{{ userData?.lrfm?.monetary_value_currency ?? 0 }}</span>
      </div>
    </div>

    <div class="border-b border-[#ddd] my-6"></div>

    <AdditionalInfoForm :userData="userData?.info" :uuid="userData?.info?.uuid ?? ''"
      @refreshUserData="refetchUserData" />

    <div class="border-b border-[#ddd] my-6"></div>

    <div v-if="!!userData?.detail">
      <MobileForm :userData="userData?.detail" :uuid="userData?.info?.uuid ?? ''" @refreshUserData="refetchUserData" />

      <EmailForm :userData="userData?.detail ?? {}" />

      <NationalCodeForm :userData="userData?.detail" :uuid="userData?.info?.uuid ?? ''"
        @refreshUserData="refetchUserData" />

      <IdentityDocumentForm :userData="userData?.detail" :uuid="userData?.info?.uuid ?? ''"
        @refreshUserData="refetchUserData" />

      <SelfieForm :userData="userData?.detail" :uuid="userData?.info?.uuid ?? ''" @refreshUserData="refetchUserData" />

      <RegistrationStatusForm :userData="userData?.detail" :userStatusOptions="userStatusOptions"
        :uuid="userData?.info?.uuid ?? ''" @refreshUserData="refetchUserData" />

    </div>
  </div>
</template>



<style scoped>
.pop-enter-active,
.pop-leave-active {
  transition: all 0.3s cubic-bezier(0.45, 0, 0.55, 1);
  transform-origin: center center;
}

.pop-enter-from {
  opacity: 0;
  transform: scale(0);
}

.pop-enter-to {
  opacity: 1;
  transform: scale(1);
}

.pop-leave-from {
  opacity: 1;
  transform: scale(1);
}

.pop-leave-to {
  opacity: 0;
  transform: scale(0);
}
</style>
